import commonService from "../services/common.service";
import config from "../config";

function dataUrlToFile(dataUrl, filename) {
  const [meta, base64] = dataUrl.split(",");
  const mime = meta.match(/data:(.*?);base64/)?.[1] || "image/png";
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  const extension = mime.split("/")[1] || "png";
  return new File([bytes], `${filename}.${extension}`, { type: mime });
}

export async function uploadImageFile(file) {
  const result = await commonService.uploadImage(file);
  if (!result || result.error) {
    throw result?.error || new Error("Image upload failed");
  }

  const url = typeof result === "string"
    ? result
    : result.location || result.url || result.data;

  if (!url) {
    throw new Error("Image upload did not return a URL");
  }

  if (url.startsWith("//")) {
    return `${window.location.protocol}${url}`;
  }

  if (url.startsWith("/")) {
    return `${config.get("endpoint")}${url}`;
  }

  return url;
}

export async function uploadBase64Images(html = "") {
  if (!html.includes("data:image")) return html;

  const container = document.createElement("div");
  container.innerHTML = html;
  const images = Array.from(container.querySelectorAll("img"));

  for (const [index, image] of images.entries()) {
    const src = image.getAttribute("src");
    if (!src?.startsWith("data:image")) continue;

    const file = dataUrlToFile(src, `blog-image-${Date.now()}-${index}`);
    const url = await uploadImageFile(file);
    image.setAttribute("src", url);
  }

  return container.innerHTML;
}

export function normalizeEditorInputHtml(html = "") {
  if (!html) return "";

  const container = document.createElement("div");
  container.innerHTML = html;

  // Ignore formatting-only whitespace between top-level blocks while
  // preserving whitespace and explicit <br> elements inside the content.
  Array.from(container.childNodes).forEach((node) => {
    if (node.nodeType === 3 && !node.textContent.trim()) {
      node.remove();
    }
  });

  return container.innerHTML.trim();
}

export function normalizeEditorHtml(html = "") {
  if (!html) return "";

  const container = document.createElement("div");
  container.innerHTML = normalizeEditorInputHtml(html);

  // Quill adds one empty paragraph before non-paragraph blocks on each HTML
  // round-trip. Remove exactly one adjacent empty line before saving, while
  // preserving any additional blank lines intentionally entered by the user.
  container
    .querySelectorAll(
      "h1, h2, h3, h4, h5, h6, blockquote, pre, ul, ol, table, figure, hr",
    )
    .forEach((block) => {
      const previous = block.previousElementSibling;
      if (!previous || previous.tagName !== "P") return;

      const childElements = Array.from(previous.children);
      const breakElements = childElements.filter((child) => child.tagName === "BR");
      const otherElements = childElements.filter((child) => child.tagName !== "BR");
      const text = (previous.textContent || "")
        .replace(/[\u00a0\u200b-\u200d\ufeff]/g, "")
        .trim();

      if (breakElements.length === 1 && otherElements.length === 0 && !text) {
        previous.remove();
      }
    });

  // A bare domain is treated as a relative URL by the browser. Store a fully
  // usable URL while continuing to support site-relative and anchor links.
  container.querySelectorAll("a[href]").forEach((anchor) => {
    const href = (anchor.getAttribute("href") || "").trim();
    if (/^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[/:?#]|$)/i.test(href)) {
      anchor.setAttribute("href", `https://${href}`);
    }
  });

  return container.innerHTML.trim();
}
